"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { RequiredMark } from "../required-mark";
import { cn } from "../utils";
export type MinuteInterval = 1 | 5 | 15 | 30;
export interface InputTimeProps {
  name: string;
  id?: string;
  label?: string | null;
  /** Helper text rendered below the field — plain string or rich content (e.g. a description with a link) */
  description?: React.ReactNode;
  value?: string;
  onChange?: (time: string) => void;
  onBlur?: () => void;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  minuteInterval?: MinuteInterval;
  className?: string;
}
const HOUR_MAX = 23;
const MINUTE_MAX = 59;
function pad2(n: number): string {
  return String(n).padStart(2, "0");
}
function wrap(value: number, modulus: number): number {
  return (value % modulus + modulus) % modulus;
}
function snapMinuteToInterval(value: number, interval: number): number {
  const wrapped = wrap(value, MINUTE_MAX + 1);
  const snapped = Math.round(wrapped / interval) * interval;
  return snapped > MINUTE_MAX ? 0 : snapped;
}
function parseTime(value: string | undefined): {
  h: string;
  m: string;
} {
  if (!value) return {
    h: "",
    m: ""
  };
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) return {
    h: "",
    m: ""
  };
  return {
    h: match[1] ?? "",
    m: match[2] ?? ""
  };
}
export const InputTime = React.forwardRef<HTMLDivElement, InputTimeProps>((t0, ref) => {
  const $ = _c(163);
  const {
    name,
    id,
    label,
    description,
    value,
    onChange,
    onBlur,
    required,
    error,
    disabled,
    minuteInterval: t1,
    className
  } = t0;
  const minuteInterval = t1 === undefined ? 15 : t1;
  const generatedId = React.useId();
  const baseId = id || name || generatedId;
  const hourId = `${baseId}-hour`;
  const minuteId = `${baseId}-minute`;
  const descriptionId = `${baseId}-description`;
  const hourInputRef = React.useRef(null);
  const minuteInputRef = React.useRef(null);
  const containerRef = React.useRef(null);
  let t2;
  if ($[0] !== ref) {
    t2 = node => {
      containerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
    };
    $[0] = ref;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const setContainerRef = t2;
  let t3;
  if ($[2] !== value) {
    t3 = () => parseTime(value).h;
    $[2] = value;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [hourRaw, setHourRaw] = React.useState(t3);
  let t4;
  if ($[4] !== value) {
    t4 = () => parseTime(value).m;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [minuteRaw, setMinuteRaw] = React.useState(t4);
  let t5;
  let t6;
  if ($[6] !== value) {
    t5 = () => {
      const parsed = parseTime(value);
      setHourRaw(parsed.h);
      setMinuteRaw(parsed.m);
    };
    t6 = [value];
    $[6] = value;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  React.useEffect(t5, t6);
  const hasError = Boolean(error);
  const hasDescription = Boolean(error || description);
  let t7;
  if ($[9] !== onChange) {
    t7 = (h, m) => {
      if (h === "" && m === "") {
        onChange?.("");
        return;
      }
      const hN = Number(h);
      const mN = Number(m);
      const valid = h !== "" && m !== "" && Number.isFinite(hN) && Number.isFinite(mN) && hN >= 0 && hN <= HOUR_MAX && mN >= 0 && mN <= MINUTE_MAX;
      onChange?.(valid ? `${pad2(hN)}:${pad2(mN)}` : "");
    };
    $[9] = onChange;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const emit = t7;
  let t8;
  if ($[11] !== onBlur) {
    t8 = relatedTarget => {
      if (!containerRef.current) {
        return;
      }
      if (relatedTarget instanceof Node && containerRef.current.contains(relatedTarget)) {
        return;
      }
      onBlur?.();
    };
    $[11] = onBlur;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const fireBlurIfLeavingControl = t8;
  let t9;
  if ($[13] !== emit || $[14] !== minuteRaw) {
    t9 = event => {
      const next = event.target.value.replace(/\D/g, "").slice(0, 2);
      setHourRaw(next);
      emit(next, minuteRaw);
      if (next.length === 2 && Number(next) <= HOUR_MAX) {
        minuteInputRef.current?.focus();
        minuteInputRef.current?.select();
      }
    };
    $[13] = emit;
    $[14] = minuteRaw;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleHourChange = t9;
  let t10;
  if ($[16] !== emit || $[17] !== hourRaw) {
    t10 = event_0 => {
      const next_0 = event_0.target.value.replace(/\D/g, "").slice(0, 2);
      setMinuteRaw(next_0);
      emit(hourRaw, next_0);
    };
    $[16] = emit;
    $[17] = hourRaw;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const handleMinuteChange = t10;
  let t11;
  if ($[19] !== emit || $[20] !== fireBlurIfLeavingControl || $[21] !== minuteRaw) {
    t11 = event_1 => {
      const current = event_1.currentTarget.value;
      if (current !== "") {
        const wrapped = wrap(Number(current), HOUR_MAX + 1);
        const padded = pad2(wrapped);
        if (padded !== current) {
          setHourRaw(padded);
          emit(padded, minuteRaw);
        }
      }
      fireBlurIfLeavingControl(event_1.relatedTarget);
    };
    $[19] = emit;
    $[20] = fireBlurIfLeavingControl;
    $[21] = minuteRaw;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const handleHourBlur = t11;
  let t12;
  if ($[23] !== emit || $[24] !== fireBlurIfLeavingControl || $[25] !== hourRaw || $[26] !== minuteInterval) {
    t12 = event_2 => {
      const current_0 = event_2.currentTarget.value;
      if (current_0 !== "") {
        const snapped = snapMinuteToInterval(Number(current_0), minuteInterval);
        const padded_0 = pad2(snapped);
        if (padded_0 !== current_0) {
          setMinuteRaw(padded_0);
          emit(hourRaw, padded_0);
        }
      }
      fireBlurIfLeavingControl(event_2.relatedTarget);
    };
    $[23] = emit;
    $[24] = fireBlurIfLeavingControl;
    $[25] = hourRaw;
    $[26] = minuteInterval;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const handleMinuteBlur = t12;
  let t13;
  if ($[28] !== emit || $[29] !== hourRaw || $[30] !== minuteRaw) {
    t13 = delta => {
      const current_1 = hourRaw === "" ? 0 : Number(hourRaw);
      const next_1 = wrap(current_1 + delta, HOUR_MAX + 1);
      const padded_1 = pad2(next_1);
      setHourRaw(padded_1);
      emit(padded_1, minuteRaw);
    };
    $[28] = emit;
    $[29] = hourRaw;
    $[30] = minuteRaw;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const stepHour = t13;
  let t14;
  if ($[32] !== emit || $[33] !== hourRaw || $[34] !== minuteInterval || $[35] !== minuteRaw) {
    t14 = delta_0 => {
      const current_2 = minuteRaw === "" ? 0 : Number(minuteRaw);
      const next_2 = wrap(current_2 + delta_0 * minuteInterval, MINUTE_MAX + 1);
      const padded_2 = pad2(next_2);
      setMinuteRaw(padded_2);
      emit(hourRaw, padded_2);
    };
    $[32] = emit;
    $[33] = hourRaw;
    $[34] = minuteInterval;
    $[35] = minuteRaw;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const stepMinute = t14;
  let t15;
  if ($[37] !== stepHour) {
    t15 = event_3 => {
      if (event_3.key === "ArrowUp") {
        event_3.preventDefault();
        stepHour(1);
      } else {
        if (event_3.key === "ArrowDown") {
          event_3.preventDefault();
          stepHour(-1);
        }
      }
    };
    $[37] = stepHour;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const handleHourKeyDown = t15;
  let t16;
  if ($[39] !== stepMinute) {
    t16 = event_4 => {
      if (event_4.key === "ArrowUp") {
        event_4.preventDefault();
        stepMinute(1);
      } else {
        if (event_4.key === "ArrowDown") {
          event_4.preventDefault();
          stepMinute(-1);
        }
      }
    };
    $[39] = stepMinute;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const handleMinuteKeyDown = t16;
  const t17 = hasError && "bg-input-surface-error hover:bg-input-surface-error focus:bg-input-surface-error";
  const t18 = disabled && "pointer-events-none bg-input-surface-disabled text-input-text-disabled";
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = cn("h-12 w-full rounded-sm border border-transparent pl-4 pr-10 py-3", "typography-body-sm-regular", "text-input-text-rest placeholder:text-input-text-disabled", "bg-input-surface-rest", "outline-none transition-colors text-center tabular-nums", "hover:bg-input-surface-hover", "focus:border-border-subtle focus:bg-input-surface-hover", t17, t18);
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const fieldChrome = t19;
  const t20 = disabled && "pointer-events-none text-input-text-disabled";
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  let t33;
  let t34;
  let t35;
  let t36;
  let t37;
  if ($[44] !== className || $[45] !== descriptionId || $[46] !== disabled || $[47] !== fieldChrome || $[48] !== handleHourBlur || $[49] !== handleHourChange || $[50] !== handleHourKeyDown || $[51] !== handleMinuteBlur || $[52] !== handleMinuteChange || $[53] !== handleMinuteKeyDown || $[54] !== hasDescription || $[55] !== hasError || $[56] !== hourId || $[57] !== hourRaw || $[58] !== label || $[59] !== minuteId || $[60] !== minuteInterval || $[61] !== minuteRaw || $[62] !== required || $[63] !== setContainerRef || $[64] !== stepHour || $[65] !== stepMinute || $[66] !== t20) {
    const stepperButton = cn("flex size-4 items-center justify-center text-text-secondary transition-colors", "hover:text-text-primary", "focus-visible:outline-none focus-visible:text-text-primary", t20);
    t34 = setContainerRef;
    t35 = "input-time";
    if ($[84] !== className) {
      t36 = cn("group/input-time flex w-full flex-col gap-2", className);
      $[84] = className;
      $[85] = t36;
    } else {
      t36 = $[85];
    }
    if ($[86] !== disabled || $[87] !== hourId || $[88] !== label || $[89] !== required) {
      t37 = Boolean(label) && <label htmlFor={hourId} className={cn("typography-component-sm-medium desktop:typography-component-md-medium", disabled ? "text-input-text-disabled" : "text-input-text-label")}>{label}<RequiredMark required={required} /></label>;
      $[86] = disabled;
      $[87] = hourId;
      $[88] = label;
      $[89] = required;
      $[90] = t37;
    } else {
      t37 = $[90];
    }
    t31 = "flex w-full items-stretch gap-x-6";
    t32 = hasDescription ? descriptionId : undefined;
    const t38 = hasError || undefined;
    let t39;
    if ($[91] !== disabled || $[92] !== fieldChrome || $[93] !== handleHourBlur || $[94] !== handleHourChange || $[95] !== handleHourKeyDown || $[96] !== hourId || $[97] !== hourRaw || $[98] !== required || $[99] !== t38) {
      t39 = <input ref={hourInputRef} id={hourId} type="text" inputMode="numeric" autoComplete="off" maxLength={2} placeholder="HH" value={hourRaw} disabled={disabled} required={required} aria-label="Hours" aria-invalid={t38} onChange={handleHourChange} onKeyDown={handleHourKeyDown} onBlur={handleHourBlur} onFocus={_temp} className={fieldChrome} />;
      $[91] = disabled;
      $[92] = fieldChrome;
      $[93] = handleHourBlur;
      $[94] = handleHourChange;
      $[95] = handleHourKeyDown;
      $[96] = hourId;
      $[97] = hourRaw;
      $[98] = required;
      $[99] = t38;
      $[100] = t39;
    } else {
      t39 = $[100];
    }
    let t40;
    if ($[101] !== stepHour) {
      t40 = () => {
        stepHour(1);
        hourInputRef.current?.focus();
      };
      $[101] = stepHour;
      $[102] = t40;
    } else {
      t40 = $[102];
    }
    let t41;
    if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
      t41 = <ChevronUp aria-hidden={true} className="size-4" />;
      $[103] = t41;
    } else {
      t41 = $[103];
    }
    let t42;
    if ($[104] !== stepHour) {
      t42 = () => {
        stepHour(-1);
        hourInputRef.current?.focus();
      };
      $[104] = stepHour;
      $[105] = t42;
    } else {
      t42 = $[105];
    }
    let t43;
    if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
      t43 = <ChevronDown aria-hidden={true} className="size-4" />;
      $[106] = t43;
    } else {
      t43 = $[106];
    }
    const t44 = <div className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 flex-col gap-0.5"><button type="button" tabIndex={-1} aria-label="Increase hours" disabled={disabled} onClick={t40} className={cn(stepperButton, "pointer-events-auto")}>{t41}</button><button type="button" tabIndex={-1} aria-label="Decrease hours" disabled={disabled} onClick={t42} className={cn(stepperButton, "pointer-events-auto")}>{t43}</button></div>;
    if ($[107] !== t39 || $[108] !== t44) {
      t33 = <div className="relative flex-1">{t39}{t44}</div>;
      $[107] = t39;
      $[108] = t44;
      $[109] = t33;
    } else {
      t33 = $[109];
    }
    t29 = "relative flex-1";
    const t45 = hasError || undefined;
    if ($[110] !== disabled || $[111] !== fieldChrome || $[112] !== handleMinuteBlur || $[113] !== handleMinuteChange || $[114] !== handleMinuteKeyDown || $[115] !== minuteId || $[116] !== minuteRaw || $[117] !== required || $[118] !== t45) {
      t30 = <input ref={minuteInputRef} id={minuteId} type="text" inputMode="numeric" autoComplete="off" maxLength={2} placeholder="MM" value={minuteRaw} disabled={disabled} required={required} aria-label="Minutes" aria-invalid={t45} onChange={handleMinuteChange} onKeyDown={handleMinuteKeyDown} onBlur={handleMinuteBlur} onFocus={_temp2} className={fieldChrome} />;
      $[110] = disabled;
      $[111] = fieldChrome;
      $[112] = handleMinuteBlur;
      $[113] = handleMinuteChange;
      $[114] = handleMinuteKeyDown;
      $[115] = minuteId;
      $[116] = minuteRaw;
      $[117] = required;
      $[118] = t45;
      $[119] = t30;
    } else {
      t30 = $[119];
    }
    t27 = "pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 flex-col gap-0.5";
    let t46;
    if ($[120] !== stepMinute) {
      t46 = () => {
        stepMinute(1);
        minuteInputRef.current?.focus();
      };
      $[120] = stepMinute;
      $[121] = t46;
    } else {
      t46 = $[121];
    }
    let t47;
    if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = <ChevronUp aria-hidden={true} className="size-4" />;
      $[122] = t47;
    } else {
      t47 = $[122];
    }
    t28 = <button type="button" tabIndex={-1} aria-label={`Increase minutes by ${minuteInterval}`} disabled={disabled} onClick={t46} className={cn(stepperButton, "pointer-events-auto")}>{t47}</button>;
    t21 = "button";
    t22 = -1;
    t23 = `Decrease minutes by ${minuteInterval}`;
    t24 = disabled;
    if ($[123] !== stepMinute) {
      t25 = () => {
        stepMinute(-1);
        minuteInputRef.current?.focus();
      };
      $[123] = stepMinute;
      $[124] = t25;
    } else {
      t25 = $[124];
    }
    t26 = cn(stepperButton, "pointer-events-auto");
    $[44] = className;
    $[45] = descriptionId;
    $[46] = disabled;
    $[47] = fieldChrome;
    $[48] = handleHourBlur;
    $[49] = handleHourChange;
    $[50] = handleHourKeyDown;
    $[51] = handleMinuteBlur;
    $[52] = handleMinuteChange;
    $[53] = handleMinuteKeyDown;
    $[54] = hasDescription;
    $[55] = hasError;
    $[56] = hourId;
    $[57] = hourRaw;
    $[58] = label;
    $[59] = minuteId;
    $[60] = minuteInterval;
    $[61] = minuteRaw;
    $[62] = required;
    $[63] = setContainerRef;
    $[64] = stepHour;
    $[65] = stepMinute;
    $[66] = t20;
    $[67] = t21;
    $[68] = t22;
    $[69] = t23;
    $[70] = t24;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
    $[74] = t28;
    $[75] = t29;
    $[76] = t30;
    $[77] = t31;
    $[78] = t32;
    $[79] = t33;
    $[80] = t34;
    $[81] = t35;
    $[82] = t36;
    $[83] = t37;
  } else {
    t21 = $[67];
    t22 = $[68];
    t23 = $[69];
    t24 = $[70];
    t25 = $[71];
    t26 = $[72];
    t27 = $[73];
    t28 = $[74];
    t29 = $[75];
    t30 = $[76];
    t31 = $[77];
    t32 = $[78];
    t33 = $[79];
    t34 = $[80];
    t35 = $[81];
    t36 = $[82];
    t37 = $[83];
  }
  let t38;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <ChevronDown aria-hidden={true} className="size-4" />;
    $[125] = t38;
  } else {
    t38 = $[125];
  }
  let t39;
  if ($[126] !== t21 || $[127] !== t22 || $[128] !== t23 || $[129] !== t24 || $[130] !== t25 || $[131] !== t26) {
    t39 = <button type={t21} tabIndex={t22} aria-label={t23} disabled={t24} onClick={t25} className={t26}>{t38}</button>;
    $[126] = t21;
    $[127] = t22;
    $[128] = t23;
    $[129] = t24;
    $[130] = t25;
    $[131] = t26;
    $[132] = t39;
  } else {
    t39 = $[132];
  }
  let t40;
  if ($[133] !== t27 || $[134] !== t28 || $[135] !== t39) {
    t40 = <div className={t27}>{t28}{t39}</div>;
    $[133] = t27;
    $[134] = t28;
    $[135] = t39;
    $[136] = t40;
  } else {
    t40 = $[136];
  }
  let t41;
  if ($[137] !== t29 || $[138] !== t30 || $[139] !== t40) {
    t41 = <div className={t29}>{t30}{t40}</div>;
    $[137] = t29;
    $[138] = t30;
    $[139] = t40;
    $[140] = t41;
  } else {
    t41 = $[140];
  }
  let t42;
  if ($[141] !== t31 || $[142] !== t32 || $[143] !== t33 || $[144] !== t41) {
    t42 = <div className={t31} aria-describedby={t32}>{t33}{t41}</div>;
    $[141] = t31;
    $[142] = t32;
    $[143] = t33;
    $[144] = t41;
    $[145] = t42;
  } else {
    t42 = $[145];
  }
  const t43 = value ?? "";
  let t44;
  if ($[146] !== name || $[147] !== t43) {
    t44 = <input type="hidden" name={name} value={t43} />;
    $[146] = name;
    $[147] = t43;
    $[148] = t44;
  } else {
    t44 = $[148];
  }
  let t45;
  if ($[149] !== description || $[150] !== descriptionId || $[151] !== error || $[152] !== hasDescription || $[153] !== hasError) {
    t45 = hasDescription && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>{error || description}</p>;
    $[149] = description;
    $[150] = descriptionId;
    $[151] = error;
    $[152] = hasDescription;
    $[153] = hasError;
    $[154] = t45;
  } else {
    t45 = $[154];
  }
  let t46;
  if ($[155] !== t34 || $[156] !== t35 || $[157] !== t36 || $[158] !== t37 || $[159] !== t42 || $[160] !== t44 || $[161] !== t45) {
    t46 = <div ref={t34} data-testid={t35} className={t36}>{t37}{t42}{t44}{t45}</div>;
    $[155] = t34;
    $[156] = t35;
    $[157] = t36;
    $[158] = t37;
    $[159] = t42;
    $[160] = t44;
    $[161] = t45;
    $[162] = t46;
  } else {
    t46 = $[162];
  }
  return t46;
});
InputTime.displayName = "InputTime";
function _temp(event_5) {
  return event_5.currentTarget.select();
}
function _temp2(event_6) {
  return event_6.currentTarget.select();
}